import { computed, ref } from 'vue'
import { isFunction } from '@/global/utils/utils'
import { api } from '@/global/services/api'
import { registeredTableHandlers } from '@/global/components/view-layouts/tabs-layout/config/registered-table-handlers'
import { useTableService } from '@/global/composables/table/useTableService'
import { deleteAction } from '@/global/components/table/helpers/tableActions'
import { dateService, FORMATS } from '@/global/services/date'
import { WINTER_SERVICE_MODULE } from '@/modules/road-maintenance-module/winter-service-v2/common/config/constants'
import {
  BOOK_ENDPOINT,
  BOOK_TABLE_KEY,
  BOOK_TRANSLATION_NAMESPACE as NS,
  BOOK_UI_VIEW,
  buildBookPdfEndpoint
} from '@/modules/road-maintenance-module/winter-service-v2/book/config/constants'

const { buildConfig } = useTableService()

const API_CONFIG = {
  get: {
    module: WINTER_SERVICE_MODULE,
    route: BOOK_ENDPOINT
  }
}

/**
 * The winter service book listing: the books that have been made, and the button that
 * starts a new one.
 *
 * A book is not edited from here. It is a set of tasks that was picked at a moment in
 * time and printed, so the row offers what can still be done with it - taking the PDF
 * again, or throwing the book away.
 */
export function useTable (options) {
  const {
    trans,
    startPickingFn,
    openInfoDialogFn,
    closeInfoDialogFn,
    setInfoDialogLoaderFn
  } = options || {}

  // One PDF at a time: it is rendered on request, and a second press while it is being
  // drawn would only queue another render of the same book.
  const isDownloading = ref(false)

  const fetchTableData = () => {
    const { fetchData } = registeredTableHandlers?.[BOOK_TABLE_KEY] || {}
    if (!isFunction(fetchData)) {
      return
    }
    fetchData()
  }

  const downloadPdf = async (event) => {
    const { id, title } = event?.data || {}

    if (!id || isDownloading.value) {
      return
    }

    let url = null

    try {
      isDownloading.value = true

      const response = await api(WINTER_SERVICE_MODULE).get(
        buildBookPdfEndpoint(id),
        null,
        { responseType: 'blob' }
      )

      url = window.URL.createObjectURL(new Blob([response], { type: 'application/pdf' }))

      const link = document.createElement('a')
      link.href = url
      // The book's own name, so a folder of downloads stays readable.
      link.setAttribute('download', `${title || `knjiga-${id}`}.pdf`)
      document.body.appendChild(link)
      link.click()
      link.remove()
    }
    catch (exception) {
      console.error(exception)
    }
    finally {
      if (url) {
        window.URL.revokeObjectURL(url)
      }

      isDownloading.value = false
    }
  }

  const deleteBook = async (event) => {
    try {
      const { id } = event?.data || {}
      if (!id) {
        return
      }

      setInfoDialogLoaderFn?.(true)
      await api(WINTER_SERVICE_MODULE).delete(`${BOOK_ENDPOINT}/${id}`)
      closeInfoDialogFn?.()
      fetchTableData()
    }
    catch (exception) {
      console.error(exception)
    }
    finally {
      setInfoDialogLoaderFn?.(false)
    }
  }

  const onBookDelete = (event) => {
    openInfoDialogFn?.({
      title: trans(`${NS}.delete_confirmation_title`),
      message: trans(`${NS}.delete_confirmation_message`),
      onConfirm: () => deleteBook(event)
    })
  }

  const cellsConfig = computed(() => ({
    created_at: {
      custom_display_value: (data) => data?.created_at
        ? dateService.formatCustom(data.created_at, FORMATS.isoDatetime, FORMATS.date)
        : '-'
    },
    task_count: {
      custom_display_value: (data) => data?.task_count ?? 0
    }
  }))

  const headers = computed(() => ([
    {
      label: trans(`${NS}.id`),
      key: 'id',
      always_visible: true
    },
    {
      label: trans(`${NS}.creation_date`),
      key: 'created_at',
      sortable: true,
      always_visible: true
    },
    {
      label: trans(`${NS}.task_count`),
      key: 'task_count',
      always_visible: true
    },
    {
      label: trans(`${NS}.book_title`),
      key: 'title',
      sortable: true,
      always_visible: true
    }
  ]))

  const filtersConfig = computed(() => ({
    fields: [
      {
        key: 'title',
        label: trans(`${NS}.book_title`)
      }
    ]
  }))

  const actionsConfig = computed(() => ([
    {
      label: trans(`${NS}.download_pdf`),
      icon: 'mdi-file-pdf-box',
      click: downloadPdf
    },
    deleteAction(trans, onBookDelete)
  ]))

  const breadcrumbs = computed(() => ({
    items: [
      {
        text: trans('road-maintenance/winter_service2.winter_service_book')
      },
      {
        text: trans(`${NS}.overview`),
        bolded: true
      }
    ]
  }))

  const config = computed(() => ({
    tableConfig: buildConfig({
      key: BOOK_TABLE_KEY,
      uiView: BOOK_UI_VIEW,
      apiConfig: API_CONFIG,
      // Rendering the PDF takes as long as it takes, and the row's menu closes behind
      // it - the table's own loader is what shows the screen is busy with it.
      showLoader: isDownloading.value,
      search_and_buttons_config: {
        search_field_config: {
          placeholder: trans('base.tracker_search'),
          prependInnerIcon: 'mdi-magnify'
        },
        // A new book is not a form to fill in but a set of tasks to pick, so the button
        // hands the screen over to the picking table rather than opening a dialog.
        showNewBtn: true,
        newClick: () => startPickingFn?.(),
        newButtonConfig: {
          label: trans(`${NS}.new_book`)
        }
      },
      tableConfig: {
        cellsConfig: cellsConfig.value,
        headers: headers.value,
        actionsConfig: actionsConfig.value,
        filtersConfig: filtersConfig.value
      }
    }),
    breadcrumbs: breadcrumbs.value
  }))

  return {
    config,
    fetchTableData
  }
}
